/**
 * Bulk order updater for assignment-related changes via Postgres.
 * @module bulkOrderUpdateForAssignment
 */

import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { getBearerToken } from "../../postCall/bearerToken";

/** Parameters for bulk-updating orders in Postgres. */
export interface BulkOrderUpdateForAssignmentParams<T> {
  /** List of order IDs to update. */
  order_ids: string[];
  /** Organisation scoping the update. */
  org_id: string;
  /**
   * Data applied to each selected order. The dedicated assignment endpoint
   * accepts only `assigned_to_id` and `assigned_to_name` (both required, nullable).
   */
  data: T;
  /**
   * Defaults to false: use /medical/frontend-query/update/bulk/order.
   * True selects /medical/frontend-query/update/bulk/assignee, which includes
   * eligible direct siblings. This client option is not sent in the JSON body.
   */
  useAssignmentEndpoint?: boolean;
}

/** Response fields shared by the generic bulk update and assignment endpoints. */
export interface BulkOrderUpdateForAssignmentResponse {
  success: boolean;
  updated_rows: number;
}

/**
 * Sends a bulk update request for multiple medical PA orders in Postgres.
 *
 * Uses the generic bulk update endpoint unless `params.useAssignmentEndpoint`
 * is true. The dedicated assignment endpoint resolves eligible siblings and
 * updates all selected orders atomically; `updated_rows` can therefore exceed
 * the number of supplied order IDs.
 *
 * @typeParam T - Shape of the update payload applied to each order.
 * @param params - Order IDs, org ID, update data, and optional endpoint selection.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns The update result indicating success and number of affected rows.
 * @throws Re-throws any network or parsing error after logging.
 */
export const bulkOrderUpdateForAssignment = async <T>(
  params: BulkOrderUpdateForAssignmentParams<T>,
  signal?: AbortSignal,
): Promise<BulkOrderUpdateForAssignmentResponse> => {
  try {
    const url = params.useAssignmentEndpoint
      ? API_ENDPOINTS.ORDERS_BULK_ASSIGN
      : API_ENDPOINTS.ORDERS_BULK_UPDATE;
    const bearerToken = await getBearerToken();
    const raw = JSON.stringify({
      order_ids: params.order_ids,
      org_id: params.org_id,
      data: params.data,
    });
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body: raw,
      ...(signal && { signal }),
    });
    const result: BulkOrderUpdateForAssignmentResponse = await response.json();
    return result;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};
